// frontend/src/pages/PersonaPage.js
//
// Renders /for/salons and /for/agencies from data/personas.json.
//
// Targets:
//   social media marketing for salons / beauty salons  100-1K  $16.85  comp 8
//   social media management tools for agencies         100-1K  $12.00  comp 10
//   social media management for agencies               1K-10K  $5.73   comp 16
//
// The persona cluster carries the highest median bid of any cluster in the
// dataset ($10.84 after round-2 research).
//
// One component, but the CONTENT per persona is written separately and shares
// no phrasing. Persona pages are where thin content creeps in — ten pages that
// swap "salons" for "gyms" get classified as doorway pages and can drag the
// whole domain down. A salon's problem (chairs empty on quiet days, nobody free
// to post mid-appointment) has nothing in common with an agency's (twelve
// clients, approvals, per-seat pricing).
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Image, Button, Card, Accordion } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import { CheckCircleFill, ExclamationTriangleFill } from 'react-bootstrap-icons'

import Seo from '../components/Seo'
import { Download } from 'react-bootstrap-icons'
import personas from '../data/personas.json'
import NotFound from './NotFound'

export default function PersonaPage () {
  // Static routes with no :param — derive from the path, not useParams.
  const { pathname } = useLocation()
  const slug = pathname.replace(/^\/+for\/+|\/+$/g, '')
  const p = personas.find(x => x.slug === slug)
  if (!p) return <NotFound />

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: p.faqs.map(([q, a]) => ({
      '@type': 'Question', name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  }

  return (
    <>
      <Seo
        title={p.seoTitle}
        description={p.seoDescription}
        path={`/for/${p.slug}`}
        jsonLd={[faqLd]}
      />

      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='align-items-center g-5'>
            <Col md={6}>
              <h1 className='display-5 fw-bold mb-3'>{p.h1}</h1>
              <p className='lead mb-4'>{p.sub}</p>
              <Button variant='primary' size='lg' href='/register' className='me-2 mb-2'>
                Start free trial
              </Button>
              <Button variant='outline-primary' size='lg' href='/plans' className='mb-2'>
                See plans
              </Button>
              <p className='text-muted small mt-3 mb-0'>14 days free · no credit card · from $9/month</p>
            </Col>
            <Col md={6}>
              <Image src={p.hero} alt={p.heroAlt} fluid className='shadow-card rounded-4' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── INTRO ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <p className='fs-5'>{p.intro}</p>
          <p className='text-muted fs-5 mb-0'>{p.intro2}</p>
        </Container>
      </section>

      {/* ───────── GUIDE SECTIONS ─────────
          Personas whose SERP is owned by 3,000-4,500 word guides get the long
          form (small business, real estate). Salons and agencies keep the short
          product shape, because their SERPs are not the same. */}
      {p.sections && p.sections.map((sec, si) => {
        const alt = si % 2 === 1
        const wrap = `py-5${alt ? ' bg-light' : ''}`

        if (sec.type === 'cta_inline') return (
          <section className={wrap} key={si}>
            <Container style={{ maxWidth: 760 }}>
              <div className='p-4 p-md-5 rounded-3'
                   style={{ background: 'linear-gradient(135deg,#1d4ed8,#2563eb)', color: '#fff' }}>
                <h2 className='fw-bold h4 mb-2'>{sec.heading}</h2>
                <p className='mb-3' style={{ color: 'rgba(255,255,255,.88)' }}>{sec.body}</p>
                <Button variant='light' href='/register' className='fw-semibold'>{sec.button}</Button>
                <span className='ms-3 small' style={{ color: 'rgba(255,255,255,.75)' }}>
                  14 days free · no card
                </span>
              </div>
            </Container>
          </section>
        )

        if (sec.type === 'template') return (
          <section className={wrap} key={si}>
            <Container style={{ maxWidth: 760 }}>
              <div className='p-4 p-md-5 rounded-3' style={{ background: '#fff', border: '2px dashed #cbd5e1' }}>
                <h2 className='fw-bold h4 mb-3'>{sec.heading}</h2>
                <p className='text-muted mb-3'>{sec.body}</p>
                <Button variant='primary' href='/assets/greatly-social-bulk-template.csv' className='me-2 mb-2'>
                  <Download className='me-2' />Download the template
                </Button>
                <Button variant='outline-primary' href='/register' className='mb-2'>
                  Start using Greatly Social free
                </Button>
                <p className='text-muted small mb-0 mt-2'>{sec.note}</p>
              </div>
            </Container>
          </section>
        )

        if (sec.type === 'table') return (
          <section className={wrap} key={si}>
            <Container fluid='lg' style={{ maxWidth: 880 }}>
              <h2 className='fw-bold mb-2'>{sec.heading}</h2>
              {sec.intro && <p className='text-muted fs-5 mb-4'>{sec.intro}</p>}
              <div className='table-responsive'>
                <table className='table table-bordered bg-white align-middle'>
                  <thead className='table-light'>
                    <tr>{sec.head.map(h => <th key={h}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {sec.rows.map((row, i) => (
                      <tr key={i}>
                        {row.map((cell, j) => (
                          <td key={j} className={j === 0 ? 'fw-semibold' : 'text-muted'}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {sec.outro && <p className='text-muted mb-0'>{sec.outro}</p>}
            </Container>
          </section>
        )

        if (sec.type === 'steps') return (
          <section className={wrap} key={si}>
            <Container style={{ maxWidth: 780 }}>
              <h2 className='fw-bold mb-2'>{sec.heading}</h2>
              {sec.intro && <p className='text-muted fs-5 mb-4'>{sec.intro}</p>}
              <ol className='fs-5 text-muted ps-3'>
                {sec.items.map(([t, b]) => (
                  <li key={t} className='mb-3'><strong className='text-dark'>{t}.</strong> {b}</li>
                ))}
              </ol>
            </Container>
          </section>
        )

        if (sec.type === 'product') return (
          <section className={wrap} key={si}>
            <Container fluid='lg' style={{ maxWidth: 880 }}>
              <h2 className='fw-bold mb-2'>{sec.heading}</h2>
              {sec.intro && <p className='text-muted fs-5 mb-4'>{sec.intro}</p>}
              <Row className='g-3'>
                {sec.items.map(([t, b]) => (
                  <Col md={6} key={t}>
                    <div className='d-flex align-items-start gap-2 mb-2'>
                      <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                      <div>
                        <div className='fw-semibold'>{t}</div>
                        <div className='text-muted small'>{b}</div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
              {sec.outro && <p className='text-muted mt-3 mb-3'>{sec.outro}</p>}
              <Button variant='primary' size='lg' href='/register' className='me-2'>
                Start using Greatly Social free
              </Button>
              <Button variant='outline-primary' size='lg' href='/plans'>See plans</Button>
            </Container>
          </section>
        )

        // list
        return (
          <section className={wrap} key={si}>
            <Container style={{ maxWidth: 780 }}>
              <h2 className='fw-bold mb-2'>{sec.heading}</h2>
              {sec.intro && <p className='text-muted fs-5 mb-4'>{sec.intro}</p>}
              {sec.items.map(([t, b]) => (
                <div className='d-flex align-items-start gap-2 mb-3' key={t}>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <div>
                    <div className='fw-semibold'>{t}</div>
                    <div className='text-muted'>{b}</div>
                  </div>
                </div>
              ))}
              {sec.outro && <p className='text-muted mb-0 mt-3'>{sec.outro}</p>}
            </Container>
          </section>
        )
      })}

      {/* ───────── THE PROBLEM ───────── */}
      {p.problems && <section className='py-5 bg-light'>
        <Container fluid='lg'>
          <Row className='justify-content-center mb-4'>
            <Col lg={8} className='text-center'>
              <h2 className='fw-bold mb-0'>{p.problems_title}</h2>
            </Col>
          </Row>
          <Row className='g-4'>
            {p.problems.map(([title, body]) => (
              <Col md={6} key={title}>
                <Card className='h-100 shadow-card'>
                  <Card.Body>
                    <div className='mb-2'><ExclamationTriangleFill className='text-warning fs-5' /></div>
                    <Card.Title className='fw-semibold h6'>{title}</Card.Title>
                    <Card.Text className='text-muted small mb-0'>{body}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>}

      {/* ───────── WHAT WORKS / WHAT YOU GET ───────── */}
      {p.content && <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 860 }}>
          <h2 className='fw-bold mb-4'>{p.content_title}</h2>
          <Row className='g-3'>
            {p.content.map(([title, body]) => (
              <Col md={6} key={title}>
                <div className='d-flex align-items-start gap-2 mb-2'>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <div>
                    <div className='fw-semibold'>{title}</div>
                    <div className='text-muted small'>{body}</div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>}

      {/* ───────── WORKFLOW ───────── */}
      {p.workflow && <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4'>{p.workflow_title}</h2>
          <ol className='fs-5 text-muted ps-3'>
            {p.workflow.map(([title, body]) => (
              <li key={title} className='mb-3'><strong>{title}.</strong> {body}</li>
            ))}
          </ol>
          <p className='text-muted mb-4'>
            Everything lands on one <a href='/calendar-view'>content calendar</a>, and a whole
            month can go up in <a href='/bulk-posting'>a single upload</a>.
          </p>
          <figure className='mb-0'>
            <Image src={p.inlineImage} alt={p.inlineImageAlt} fluid className='rounded-3 border w-100' />
            <figcaption className='text-muted small mt-2'>{p.inlineImageAlt}</figcaption>
          </figure>
        </Container>
      </section>}

      {/* ───────── SIBLING LINKS ─────────
          Added 2026-08-14 — these pages were orphans in the content map. */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 860 }}>
          <h2 className='fw-bold h4 mb-3'>Social media for other businesses</h2>
          <Row className='g-3'>
            {personas.filter(x => x.slug !== p.slug).map(x => (
              <Col md={4} key={x.slug}>
                <a href={`/for/${x.slug}`} className='d-block p-3 rounded-3 text-decoration-none h-100'
                   style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                  <div className='fw-semibold'>{x.h1}</div>
                </a>
              </Col>
            ))}
            <Col md={4}>
              <a href='/bulk-posting' className='d-block p-3 rounded-3 text-decoration-none h-100'
                 style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                <div className='fw-semibold'>Bulk Posting</div>
                <div className='text-muted small'>Queue a month in one upload</div>
              </a>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4 text-center'>Common questions</h2>
          <Accordion flush defaultActiveKey='0'>
            {p.faqs.map(([q, a], i) => (
              <Accordion.Item eventKey={String(i)} key={i}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body className='text-muted'>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto px-4' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3'>{p.cta_title}</h2>
          <p className='lead mb-4'>{p.cta_body}</p>
          <Button variant='light' size='lg' href='/register' className='me-2 mb-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-light' size='lg' href='/calendar-view' className='mb-2'>
            See the calendar
          </Button>
        </div>
      </section>
    </>
  )
}
